import React, { useState, useEffect, useCallback } from 'react';
import {
  getMemberAllRegistrations,
  getEvent,
  cancelRegistration,
  generateICS,
  getQRCodeUrl,
  fmtDateTime,
  EVENT_CATEGORIES,
} from '../../../api/eventsApi';

// ── Helpers ───────────────────────────────────────────────────────────────────
const CATEGORY_COLORS = {
  CIRCLE_TIME:          '#0D9488',
  TALK_WORKSHOP:        '#4338CA',
  SOCIAL_RECREATIONAL:  '#F97316',
  OTHER:                '#6B7280',
};

const CATEGORY_LABEL = Object.fromEntries(
  EVENT_CATEGORIES.map((c) => [c.value, c.label])
);

function statusBadge(status) {
  const map = {
    CONFIRMED:  'bb-member-badge--green',
    WAITLISTED: 'bb-member-badge--amber',
    CHECKED_IN: 'bb-member-badge--blue',
    NO_SHOW:    'bb-member-badge--red',
    CANCELLED:  'bb-member-badge--grey',
  };
  const labels = {
    CONFIRMED:  'Confirmed',
    WAITLISTED: 'Waitlisted',
    CHECKED_IN: 'Attended',
    NO_SHOW:    'No Show',
    CANCELLED:  'Cancelled',
  };
  return (
    <span className={`bb-member-badge ${map[status] || 'bb-member-badge--grey'}`}>
      {labels[status] || status}
    </span>
  );
}

function downloadICS(event) {
  const ics = generateICS(event);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(event.title || 'event').replace(/\s+/g, '_')}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

function isFuture(ts) {
  if (!ts) return false;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.getTime() > Date.now();
}

// ── Cancel Confirmation Modal ─────────────────────────────────────────────────
function CancelModal({ eventTitle, onConfirm, onClose }) {
  return (
    <div className="bb-modal-overlay">
      <div className="bb-modal" style={{ maxWidth: 400 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Cancel Registration</h3>
        <p style={{ fontSize: 14, color: '#475569', marginBottom: 20 }}>
          Are you sure you want to cancel your registration for{' '}
          <strong>{eventTitle}</strong>?
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            className="bb-btn-primary"
            style={{ background: '#6B7280', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
            onClick={onClose}
          >
            Keep Registration
          </button>
          <button
            className="bb-btn-primary"
            style={{ background: '#DC2626', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
            onClick={onConfirm}
          >
            Yes, Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Registration Card ─────────────────────────────────────────────────────────
function RegistrationCard({ registration, event, onCancel }) {
  const { status } = registration;
  const canCancel = status === 'CONFIRMED' || status === 'WAITLISTED';
  const color = CATEGORY_COLORS[event?.category] || CATEGORY_COLORS.OTHER;

  return (
    <div className="bb-member-card" style={{ marginBottom: 14 }}>
      {/* Top strip */}
      <div style={{ height: 5, background: color, borderRadius: '4px 4px 0 0', margin: '-1px -1px 14px -1px' }} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          {/* Category badge */}
          <div style={{ marginBottom: 6 }}>
            <span
              className="bb-member-badge"
              style={{ background: color, color: '#fff', border: 'none', fontSize: 11 }}
            >
              {CATEGORY_LABEL[event?.category] || event?.category || '—'}
            </span>
          </div>

          <div className="bb-member-card-title" style={{ fontSize: 15, marginBottom: 4 }}>
            {event?.title || '—'}
          </div>
          <div className="bb-member-card-meta">
            <span>{fmtDateTime(event?.start_datetime)}</span>
            {event?.location?.name && (
              <span style={{ marginLeft: 10 }}>{event.location.name}</span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {statusBadge(status)}

          {canCancel && (
            <button
              style={{
                background: 'none',
                border: '1px solid #FCA5A5',
                color: '#DC2626',
                borderRadius: 6,
                padding: '4px 12px',
                fontSize: 12,
                cursor: 'pointer',
              }}
              onClick={() => onCancel(registration, event)}
            >
              Cancel Registration
            </button>
          )}
        </div>
      </div>

      {/* Waitlist position */}
      {status === 'WAITLISTED' && registration.waitlist_position && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#92400E', background: '#FEF3C7', borderRadius: 6, padding: '6px 12px' }}>
          You are <strong>#{registration.waitlist_position}</strong> on the waitlist
        </div>
      )}

      {/* QR code for confirmed */}
      {status === 'CONFIRMED' && registration.qr_code_token && (
        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <img
            src={getQRCodeUrl(registration.qr_code_token, 160)}
            alt="Check-in QR code"
            style={{ borderRadius: 8, border: '1px solid #E2E8F0' }}
          />
          <div style={{ fontSize: 12, color: '#64748B', marginTop: 6 }}>
            Show this at the event
          </div>
        </div>
      )}

      {/* Add to Calendar */}
      {canCancel && event && (
        <div style={{ marginTop: 12 }}>
          <button
            style={{
              background: 'none',
              border: '1px solid #CBD5E1',
              borderRadius: 6,
              padding: '5px 12px',
              fontSize: 12,
              cursor: 'pointer',
              color: '#475569',
            }}
            onClick={() => downloadICS(event)}
          >
            + Add to Calendar
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function MyEventsMember({ orgId, memberId, memberName }) {
  const [items, setItems]       = useState([]);   // [{ registration, event }]
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [cancelTarget, setCancelTarget] = useState(null); // { registration, event }
  const [cancelling, setCancelling]     = useState(false);
  const [toast, setToast]       = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const regs = await getMemberAllRegistrations(orgId, memberId);
      // Fetch event details for each registration
      const enriched = await Promise.all(
        regs.map(async (reg) => {
          try {
            const event = await getEvent(orgId, reg.event_id);
            return { registration: reg, event };
          } catch (_) {
            return { registration: reg, event: null };
          }
        })
      );
      setItems(enriched);
    } catch (err) {
      setError(err.message || 'Failed to load your registrations.');
    } finally {
      setLoading(false);
    }
  }, [orgId, memberId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Grouping ───────────────────────────────────────────────────────────────
  const upcoming = items.filter(({ registration, event }) => {
    if (!['CONFIRMED', 'WAITLISTED', 'CHECKED_IN'].includes(registration.status)) return false;
    return event ? isFuture(event.start_datetime) : false;
  });

  const past = items.filter(({ registration, event }) => {
    if (registration.status === 'CANCELLED') return true;
    if (['NO_SHOW', 'CHECKED_IN'].includes(registration.status)) return true;
    if (registration.status === 'CONFIRMED' && event && !isFuture(event.start_datetime)) return true;
    return false;
  });

  // ── Cancel handler ─────────────────────────────────────────────────────────
  const handleCancelConfirm = async () => {
    const { registration, event } = cancelTarget;
    setCancelTarget(null);
    setCancelling(true);
    try {
      await cancelRegistration(orgId, registration.event_id, registration.id, 'Cancelled by member', memberId);
      showToast('Registration cancelled.');
      await loadData();
    } catch (err) {
      showToast(err.message || 'Failed to cancel registration.');
    } finally {
      setCancelling(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div className="bb-loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="bb-member-section-title">My Registrations</h2>
      <p className="bb-member-section-sub">View and manage your event registrations.</p>

      {error && <div className="bb-alert bb-alert--error">{error}</div>}

      {/* Upcoming */}
      <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', marginBottom: 12, marginTop: 4 }}>
        Upcoming
      </h3>
      {upcoming.length === 0 ? (
        <div className="bb-empty-state" style={{ padding: '20px 0', color: '#64748B', fontSize: 14 }}>
          No upcoming events. Browse events to register!
        </div>
      ) : (
        upcoming.map(({ registration, event }) => (
          <RegistrationCard
            key={registration.id}
            registration={registration}
            event={event}
            onCancel={(reg, ev) => setCancelTarget({ registration: reg, event: ev })}
          />
        ))
      )}

      {/* Past */}
      {past.length > 0 && (
        <>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1E293B', marginBottom: 12, marginTop: 24 }}>
            Past
          </h3>
          {past.map(({ registration, event }) => (
            <RegistrationCard
              key={registration.id}
              registration={registration}
              event={event}
              onCancel={() => {}}
            />
          ))}
        </>
      )}

      {/* Cancel modal */}
      {cancelTarget && (
        <CancelModal
          eventTitle={cancelTarget.event?.title || 'this event'}
          onConfirm={handleCancelConfirm}
          onClose={() => setCancelTarget(null)}
        />
      )}

      {/* Cancelling overlay */}
      {cancelling && (
        <div className="bb-modal-overlay" style={{ background: 'rgba(0,0,0,0.2)' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 30, textAlign: 'center' }}>
            <div className="bb-loading-spinner" />
            <p style={{ marginTop: 12, color: '#475569' }}>Cancelling...</p>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: '#1E293B',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: 8,
            fontSize: 14,
            zIndex: 9999,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

export default MyEventsMember;
